import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    Fda2579FormModel,
    SignaturePad,
    SignaturePadOptions,
    NotificationIdentifier,
    ConfigConstants,
    AddressModel,
} from '@evorait/pace-core';
import { map, takeUntil } from 'rxjs/operators';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Observable, Subject } from 'rxjs';
import { BsModalService } from 'ngx-bootstrap/modal';
import { cloneDeep } from 'lodash';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'pace-confirmation-fda2579-form',
    templateUrl: './confirmation-fda2579-form.component.html',
    styleUrls: ['./confirmation-fda2579-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationFDA2579FormComponent implements AfterViewInit, OnInit, OnDestroy {
    @Input()
    public model: Fda2579FormModel;

    @Input()
    public notification: NotificationIdentifier;
    private destroy$ = new Subject<void>();
    public isSignatureInvalid$: Observable<boolean>;

    @ViewChild('customerSignatureCanvasElement')
    public customerSignatureCanvasElement: ElementRef;
    public readonly signatureImageMaxSize = 9000;
    public fda2579FormGroup: FormGroup;
    public fda2579FormEquipmentChildren: FormArray;
    public fda2579FormAssemblerInfoChildren: FormArray;
    public fda2579FormGeneralInfoChildren: FormArray;
    public fda2579FormComponentInfoChildren: FormArray;
    public fda2579FormCommentsChildren: FormArray;
    public fda2579FormCertificationChildren: FormArray;

    public modalResult = new EventEmitter<Fda2579FormModel>();
    public readonly faTrashAlt = faTrashAlt;

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly formBuilder: FormBuilder,
        private bsModalService: BsModalService,
    ) {}

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public ngOnInit(): void {
        this.fda2579FormEquipmentChildren = this.formBuilder.array([]);
        this.fda2579FormAssemblerInfoChildren = this.formBuilder.array([]);
        this.fda2579FormGeneralInfoChildren = this.formBuilder.array([]);
        this.fda2579FormComponentInfoChildren = this.formBuilder.array([]);
        this.fda2579FormCommentsChildren = this.formBuilder.array([]);
        this.fda2579FormCertificationChildren = this.formBuilder.array([]);

        this.fda2579FormGroup = this.formBuilder.group({
            fdaFormEquipmentChildren: this.fda2579FormEquipmentChildren,
            fdaFormAssemblerInfoChildren: this.fda2579FormAssemblerInfoChildren,
            fdaFormGeneralInfoChildren: this.fda2579FormGeneralInfoChildren,
            fdaFormComponentInfoChildren: this.fda2579FormComponentInfoChildren,
            fdaFormCommentsChildren: this.fda2579FormCommentsChildren,
            fdaFormCertificationChildren: this.fda2579FormCertificationChildren,
        });

        const savedSignature = localStorage.getItem('customerSignature');
        if (savedSignature) {
            this.model.signature.value = savedSignature;
        }
    }

    public clearCustomerSignature(): void {
        this.model.customerSignaturePad.clear();
        this.fda2579FormGroup.updateValueAndValidity();
    }

    public onCityChange(): void {
        this.log.debug(`ConfirmationFDA2579FormComponent: onCityChange: Change in city field, deselecting statesOfUSA`);
        AddressModel.updateStateOfUSA(this.model.notificationAddress);
    }

    public saveAndCloseFdaForm(): void {
        this.model.isFda2579FormValid = this.fda2579FormGroup.status === ConfigConstants.FORM_STATUS.VALID;
        this.modalResult.emit(cloneDeep(this.model));
        this.bsModalService.hide();
    }

    public ngAfterViewInit(): void {
        const signatureOptions: SignaturePadOptions = {
            outputImageType: 'image/jpeg',
            outputImageMaxSize: this.signatureImageMaxSize,
            backgroundColor: 'rgba(255, 255, 255, 1)',
            onEnd: () => {
                this.fda2579FormCertificationChildren.updateValueAndValidity();
                // we need to add the markForCheck, so that the signatureChanges, and formValidation gets updated.
                // Otherwise, formValidation will not get updated.
                this.changeDetector.markForCheck();
            },
        };
        this.model.customerSignaturePad = new SignaturePad(
            this.log,
            <HTMLCanvasElement>this.customerSignatureCanvasElement.nativeElement,
            signatureOptions,
        );

        // Auto-restore saved strokes when component reopens
        if (this.model.signature.value) {
            try {
                const strokes = JSON.parse(this.model.signature.value);
                this.model.customerSignaturePad.restoreFromDataPoints(strokes);
            } catch (err) {
                this.log.error(
                    `ConfirmationFDA2579FormComponent: ngAfterViewInit: Error while restoring the signature ${err}`,
                );
            }
        }
        // storing the signature value everytime user draw
        // Observable to track real-time signature validity for reactive UI updates (async pipe)
        // Keeps the form and canvas style (valid/invalid) automatically in sync with signature pad state
        this.isSignatureInvalid$ = this.model.customerSignaturePad.dataPoints$.pipe(
            takeUntil(this.destroy$),
            map((points) => {
                this.model.signature.value = JSON.stringify(points);
                return this.model.customerSignaturePad.isEmpty;
            }),
        );

        // Since all the child forms for the accordions are added asynchronously, we now trigger a change detection
        // cycle asynchronously as well to update the "global" validation state
        Promise.resolve(undefined)
            .then(() => {
                this.changeDetector.detectChanges();
            })
            .catch((error) =>
                this.log.error(
                    `ConfirmationFDA2579FormComponent: ngAfterViewInit: Error while triggering change detection `,
                    JSON.stringify(error, Object.getOwnPropertyNames(error)),
                ),
            );
    }
}
